import React, { useState, useCallback } from 'react';
import { styled } from '@compiled/react';
import { getNewEditorLink } from '@jira/project-settings__project-configuration-links/src/getNewEditorLink.tsx';
import ChevronDown from '@atlaskit/icon/core/chevron-down';
import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button/standard-button';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl, defineMessages } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useChangeToOldEditorSuccessFlag } from './useChangeToOldEditorSuccessFlag.tsx';
import { DefaultWorkflowEditorDialog } from './DefaultWorkflowEditorDialog.tsx';

const messages = defineMessages({
	openInNewEditor: {
		id: 'workflow-editor-preference-settings.open-in-new-editor',
		defaultMessage: 'Open workflow in new editor',
		description: 'Link that opens workflow in new workflow editor',
	},

	changeDefaultEditor: {
		id: 'workflow-editor-preference-settings.change-default-editor',
		defaultMessage: 'Change default editor',
		description:
			'Link that allows you to switch default workflow editor to the old workflow editor',
	},

	switchEditorButton: {
		id: 'workflow-editor-preference-settings.switch-editor-button',
		defaultMessage: 'Switch editor',
		description: 'Button that allows you to open dropdown with option to switch editor',
	},
});

type Props = {
	workflowId: string;
	workflowName: string;
	editMode: 'draft' | 'live';
};

export const WorkflowEditorPreferenceDropdown = ({ workflowId, workflowName, editMode }: Props) => {
	const { formatMessage } = useIntl();
	const [showDialog, setShowDialog] = useState(false);
	const openDialog = () => setShowDialog(true);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Show flag when show old editor flag is true in session storage
	useChangeToOldEditorSuccessFlag({ openDefaultEditorDialog: openDialog });

	const fireTrackEvent = useCallback(
		(mode: string) => {
			const dropdownItemClickEvent = createAnalyticsEvent({
				action: 'click',
				actionSubject: 'dropdownItem',
			});
			fireUIAnalytics(dropdownItemClickEvent, 'workflowEditorToolbarNewEditorLink', {
				workflowLegacyEditMode: mode === 'draft' ? 'Draft' : 'Live',
			});
		},
		[createAnalyticsEvent],
	);

	return (
		<>
			<DropdownMenu
				testId="workflow-editor-preference-settings.dropdown-menu"
				placement="bottom-end"
				trigger={({ triggerRef, ...props }) => (
					<Button {...props} ref={triggerRef} iconAfter={<ChevronDown label="" size="small" />}>
						<LegacyButtonText>{formatMessage(messages.switchEditorButton)}</LegacyButtonText>
					</Button>
				)}
				shouldRenderToParent={fg('should-render-to-parent-should-be-true-helix')}
			>
				<DropdownItemGroup>
					<DropdownItem
						href={getNewEditorLink(workflowId)}
						onClick={() => fireTrackEvent(editMode)}
						UNSAFE_shouldDisableRouterLink
					>
						{formatMessage(messages.openInNewEditor)}
					</DropdownItem>
					<DropdownItem onClick={openDialog}>
						{formatMessage(messages.changeDefaultEditor)}
					</DropdownItem>
				</DropdownItemGroup>
			</DropdownMenu>
			{showDialog && (
				<DefaultWorkflowEditorDialog
					workflowName={workflowName}
					workflowId={workflowId}
					workflowPublicationStatus={editMode === 'draft' ? 'DRAFT' : 'LIVE'}
					onClose={() => setShowDialog(false)}
					onOpenDefaultEditorDialog={openDialog}
				/>
			)}
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LegacyButtonText = styled.span({
	fontWeight: token('font.weight.regular'),
});
